<template>
    <div>
        <div class="sunburst" ref="d3_root"></div>
        <div >
            <span style="color:lightgray">
                Sunburst: {{labels.level1}}
                <span v-if="labels.level2">→ {{labels.level2}}</span>
                <span v-if="labels.level3">→ {{labels.level3}}</span>
            </span>
        </div>
    </div>
</template>

<script>
    import * as d3 from "d3";
    export default {
        data() {
            return {
                categories: [],
                categories_for_display: [],
                category_name: '',
                hover_category: null,
            }
        },
        mounted() {
            if (this.chartData) {
                this.draw()
            }
        },
        computed: {
            labels: function () {
                if (!this.chartData) {
                    return {
                        level1: '',
                        level2: '',
                        level3: ''
                    }
                } else {
                    let meta = this.chartData.meta
                    return {
                        level1: meta.level1,
                        level2: meta.level2,
                        level3: meta.level3
                    }
                }
            }
        },
        watch: {
            highlighted: function (newValue,oldValue) {
              if (newValue !== oldValue) this.draw()
            },
            chartData: function (newValue) {
                if (newValue) this.draw()
            },
            height: function(newValue) {
                if (newValue) this.draw()
            },
            text_size: function(newValue) {
                if (newValue) this.draw()
            },
            hover_category: function(newValue) {
                this.draw()
            }
        },
        props: ['height','width', 'text_size', 'chartData','highlighted'],
        methods: {
            draw() {
                let diameter = this.height > this.width ? this.width : this.height
                var radius = diameter / 2;
                var midpoint_offset = this.width / 2

                const format = d3.format(",d")

                const svg_element = document.createElementNS("http://www.w3.org/2000/svg",'svg')

                let text_height_in_px = 5 + (this.text_size / 100) * 15 // Want values between 5 and 20

                const svg = d3.select(svg_element)
                    .attr("viewBox", `0 0 ${this.width} ${this.height}`)
                    .style("font", text_height_in_px + "px sans-serif")
                ;
                d3.select(svg_element)
                  .append('g')
                  .attr("transform",`translate(${midpoint_offset},${radius})`)
                ;

              const canvas = d3.select(svg_element).select('g')

              var rootNode = d3.hierarchy(this.chartData)

              rootNode.sum(function(d) {
                return d.count_as_terminal;
              });

              var partitionLayout = d3.partition()
                  .size([2 * Math.PI, radius]);

              partitionLayout(rootNode);

              var arcGenerator = d3.arc()
                  .startAngle(function(d) { return d.x0; })
                  .endAngle(function(d) { return d.x1; })
                  .innerRadius(function(d) { return d.y0; })
                  .outerRadius(function(d) { return d.y1; });

                while (this.$refs.d3_root.firstChild) {
                    this.$refs.d3_root.removeChild(this.$refs.d3_root.firstChild);
                }

                this.$refs.d3_root.append(svg.node())

              canvas
                  .selectAll('path')
                  .data(rootNode.descendants())
                  .join('path')
                  .attr("fill", d => {
                    if (d.depth===0) return "white"
                    while (d.depth > 1) d = d.parent;
                    return d.data.color;
                  })
                  .attr('stroke', d => {
                    let have_match = false
                    if (this.highlighted) {
                      this.highlighted.forEach(filter => {
                        if ((this.highlighted.length === d.depth) &&
                            (filter.field_name === d.data.field_name) &&
                            (filter.field_value === d.data.field_value)
                        ) {
                          have_match = true
                          return
                        }
                      })
                    }
                    return have_match ? "yellow" : "white"
                  })
                  .attr("fill-opacity", d => {
                    let opacity = 0.6
                    if (this.highlighted && this.highlighted.length > 0) {
                      let original_depth = d.depth
                      let matched = true
                      while (d.depth > 0) {
                        this.highlighted.forEach(filter => {
                          if (filter.field_name === d.data.field_name) {
                            if (filter.field_value !== d.data.field_value) {
                              matched = false
                            }
                          }
                        })
                        d = d.parent;
                      }
                      if (matched) {
                        opacity = original_depth >= this.highlighted.length ? 0.9 : 0.4;
                      } else {
                        opacity = 0.1
                      }
                    }
                    return opacity;
                  })
                  .on("click", (d) => {
                    let node = d
                    let args = []
                    while (node.depth>=1) {
                      let raw_type = node.data.type
                      let massaged_type = raw_type
                      if (raw_type==='group_by') {
                        massaged_type = 'group_value'
                      } else if (raw_type==='primary_field') {
                        massaged_type = 'label'
                      } else {
                        massaged_type = 'color'
                      }
                      args[massaged_type] = node.data.field_value
                      node = node.parent
                    }
                    this.$emit('stackChartClicked', args);
                  })
                  .attr('d', arcGenerator)
              ;

              canvas
                  .attr("text-anchor", "middle")
                  .attr("font-size", 10)
                  .attr("font-family", "sans-serif")
                  .selectAll("text")
                  .data(rootNode.descendants().filter(d => d.depth && (d.y0 + d.y1) / 2 * (d.x1 - d.x0) > 10))
                  .join("text")
                  .attr("transform", function(d) {
                    const x = (d.x0 + d.x1) / 2 * 180 / Math.PI;
                    const y = (d.y0 + d.y1) / 2;
                    return `rotate(${x - 90}) translate(${y},0) rotate(${x < 180 ? 0 : 180})`;
                  })
                  .attr("dy", "0.35em")
                  .text(d => d.data.field_value)
                  .on("click", (d) => {
                    let node = d
                    let args = []
                    while (node.depth>=1) {
                      let raw_type = node.data.type
                      let massaged_type = raw_type
                      if (raw_type==='group_by') {
                        massaged_type = 'group_value'
                      } else if (raw_type==='primary_field') {
                        massaged_type = 'label'
                      } else {
                        massaged_type = 'color'
                      }
                      args[massaged_type] = node.data.field_value
                      node = node.parent
                    }
                    this.$emit('stackChartClicked', args);
                  });

            },
            arcGenerator() {
              d3.arc()
                  .startAngle(function(d) { return d.x0; })
                  .endAngle(function(d) { return d.x1; })
                  .innerRadius(function(d) { return d.y0; })
                  .outerRadius(function(d) { return d.y1; });
            },
            removeElement(elementId) {
                // Removes an element from the document
                var element = document.getElementById(elementId);
                element.parentNode.removeChild(element);
            }
        }
    }
</script>